//script.js

import Utils from './core/utils';
import {
  WORLD,
  WORLD_ORIGIN,
  HOVER_MOVES,
  MAIN,
  INPUT,
  CAMERA,
  SOUND,
  POINT,
  BOOK_PAGES,
  STAGE22_AUDIO,
  BOOK_AUDIO,
} from './core/constants';

import { ImageCache } from './core/imageCache';
import {
  createCamera,
  clampCameraToWorld,
  beginCamera,
  endCamera,
} from './core/camera';

import { createImageItem } from './core/scene/ImageItem';
import { createPointItem } from './core/scene/PointItem';

import { createPreloader } from './core/preloader';
import { createInputController } from './core/inputController';
import { buildScene } from './core/buildScene';
// import { createBackground } from './core/background';

import { createSoundField } from './core/sound/soundField';
import { createSoundController } from './core/sound/soundController';
import { createStage22AudioController } from './core/sound/stage22AudioController';

import { mountStageNav } from './core/ui/stageNav';
import { mountStoryPapers } from './core/ui/storyPapers';
import { createCameraStageController } from './core/cameraStageController';
import { mountMediaControls } from './core/ui/mediaControls';
import { mountArtistAccordion } from './core/ui/artistAccordion';
import { mountBookStack } from './core/ui/bookStack';
import { mountStage52Gallery } from './core/ui/stage52Gallery';
import { mountStage0Hover } from './core/ui/stage0Hover';

import 'swiper/css';
import 'swiper/css/navigation';

// 상수
const { SCENE_IN_SPEED } = MAIN;
const SOUND_IDS = Object.keys(SOUND.SOURCES);
const BACKGROUND_URL = '/images/bg.webp';

// DOM
const canvas = document.querySelector('canvas.canvas');
const ctx = canvas.getContext('2d');

const sidebar = document.querySelector('.sidebar');

const btnZoomIn = document.querySelector('.zoom-in');
const btnZoomOut = document.querySelector('.zoom-out');
const btnSound = document.querySelector('.btn-sound');
const btnStory = document.querySelector('.story .btn-story');

const soundPanel = document.querySelector('.sound .sound-panel');
const storyPanel = document.querySelector('.story .story-panel');

const trackMarker = document.querySelector('.track-marker');

// 상태
const viewport = { width: 0, height: 0, dpr: 1 };
const camera = createCamera();

// 월드 좌표, 리사이징
const worldBounds = {
  originX: WORLD_ORIGIN.x,
  originY: WORLD_ORIGIN.y,
  w: WORLD.w,
  h: WORLD.h,
};

// 스케일
const inputConfig = { ...INPUT };

// 렌더 루프 상태
let MODE = 'preload';
let sceneAlpha = 0;
let needsRender = true;
let lastTime = performance.now();

// 스토리 모달 상태
let storyOpen = false;
let stageNav = null;

// 사운드 상태
let sound = null;
let soundUnlocked = false;
let soundEnabled = false;
let wasSoundOn = false;

// 0막 이미지 마우스 오버
let stage0HoverCtrl = null;

// 2-2막 사운드
let stage22AudioCtrl = null;

// 5-2막 갤러리
let stage52GalleryCtrl = null;

// 마커 상태
let trackMarkerPos = 0;
let trackMarkerTarget = 0;

// 비디오/오디오 상태
let mediaCtrl = null;

// 2-2막 책자
let bookStackCtrl = null;

// 마우스 월드 좌표
const mouseWorld = { x: 0, y: 0 };

// 모바일
const IS_MOBILE =
  matchMedia('(pointer: coarse)').matches || navigator.maxTouchPoints > 0;
let lastTappedPointId = null;

function invalidate() {
  needsRender = true;
}

// 리사이즈
function resize() {
  viewport.dpr = Math.min(window.devicePixelRatio || 1, 2);
  viewport.width = window.innerWidth;
  viewport.height = window.innerHeight;

  canvas.width = Math.floor(viewport.width * viewport.dpr);
  canvas.height = Math.floor(viewport.height * viewport.dpr);

  canvas.style.width = viewport.width + 'px';
  canvas.style.height = viewport.height + 'px';

  ctx.setTransform(viewport.dpr, 0, 0, viewport.dpr, 0, 0);

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'medium';

  updateMinScale();

  if (MODE === 'preload' || MODE === 'reveal') {
    fitWorldContain();
  } else {
    if (camera.scale < inputConfig.MIN_SCALE) {
      camera.scale = inputConfig.MIN_SCALE;
    }

    if (camera.ts < inputConfig.MIN_SCALE) {
      camera.ts = inputConfig.MIN_SCALE;
    }

    clampCameraToWorld(camera, viewport, worldBounds, CAMERA.ALLOW_FACTOR);
  }

  invalidate();

  stage52GalleryCtrl?.refreshAll?.();
}

// 코어
const imageCache = new ImageCache(invalidate);
const ImageItem = createImageItem(imageCache);

const PointItem = createPointItem(imageCache);

// const background = createBackground({ imageCache, src: BACKGROUND_URL });

const { scene, stageById, pointById } = buildScene({
  ImageItem,
  PointItem,
  WORLD_ORIGIN,
});

const inputController = createInputController({
  canvas,
  camera,
  viewport,
  worldBounds,
  scene,
  stageById,
  hoverMoves: HOVER_MOVES,
  inputConfig,
  cameraConfig: CAMERA,
  Utils,
  clampCameraToWorld,
  invalidate,
  enableHover: !IS_MOBILE,
});

// 막 이동 (카메라)
const cameraStageCtrl = createCameraStageController({
  camera,
  pointById,
  Utils,
  invalidate,
});

const preloader = createPreloader({
  imageCache,
  stageById,
  invalidate,
  onReveal: () => {
    if (!IS_MOBILE) {
      camera.ts = camera.scale * CAMERA.REVEAL_ZOOM_FACTOR;
      invalidate();
    } else {
      camera.ts = camera.scale;
    }
  },
});

// 사운드
const soundField = createSoundField({
  baseId: '0막',
  baseMin: SOUND.BASE_MIN,
  baseMax: SOUND.BASE_MAX,
  baseRadius: SOUND.BASE_RADIUS,
  layerRadius: SOUND.LAYER_RADIUS,
});

// 모드, 헬퍼 함수
function setMode(next) {
  MODE = next;
  sidebar?.classList.toggle('open', MODE === 'main');
  inputController.setEnabled(MODE === 'main');
  invalidate();
}

function openStory() {
  storyOpen = true;
  btnStory?.setAttribute('aria-pressed', 'true');
  storyPanel?.classList.add('open');
}

function closeStory() {
  storyOpen = false;
  btnStory?.setAttribute('aria-pressed', 'false');
  storyPanel?.classList.remove('open');

  stageNav?.reset?.();
}

function toggleStory() {
  storyOpen ? closeStory() : openStory();
}

function setTrackMarkerTarget(t01) {
  trackMarkerTarget = Utils.clamp(t01, 0, 1);
}

function updateMinScale() {
  const fit = Math.min(viewport.width / WORLD.w, viewport.height / WORLD.h);
  inputConfig.MIN_SCALE = Math.min(INPUT.MIN_SCALE, fit);
  return fit;
}

function fitWorldContain() {
  const fit = updateMinScale();

  camera.scale = fit;
  camera.ts = fit;

  camera.x = 0;
  camera.y = 0;
  camera.tx = 0;
  camera.ty = 0;

  clampCameraToWorld(camera, viewport, worldBounds, CAMERA.ALLOW_FACTOR);
  invalidate();
}

function preloadImage(src) {
  return new Promise((resolve) => {
    const img = new Image();
    img.decoding = 'async';
    img.loading = 'eager';
    img.src = src;

    if (img.complete) return resolve(true);

    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
  });
}

// 사운드
async function initSound() {
  sound = await createSoundController({
    sources: SOUND.SOURCES,
    smoothSpeed: SOUND.SMOOTH_SPEED,
  });
  await sound.preloadAll();
}

async function onToggleSound(e) {
  e?.preventDefault?.();
  if (IS_MOBILE) return;
  if (!sound) return;

  if (!soundUnlocked) {
    soundUnlocked = true;
    await sound.unlock();
  }
  soundEnabled = !soundEnabled;
  sound.setMaster(soundEnabled ? 1 : 0);
  btnSound?.setAttribute('aria-pressed', String(soundEnabled));

  soundPanel?.classList.toggle('open', soundEnabled);

  invalidate();
}

function computeSoundField() {
  const sp = inputController.getLastPointerRef();
  Utils.screenToWorldOut(mouseWorld, sp.x, sp.y, camera, viewport);

  let maxP = 0;
  let changed = false;

  soundField.computeTargets({
    mouseWorld,
    stageById: pointById,
    onProximity: (id, p) => {
      maxP = Math.max(maxP, p);

      const point = pointById.get(id);
      if (!point?.setActive) return;

      const prev = point.active;
      if (!point.active && p >= POINT.SWAP_ON) point.setActive(true);
      else if (point.active && p <= POINT.SWAP_OFF) point.setActive(false);

      if (point.active !== prev) changed = true;
    },
  });
  const prevTarget = trackMarkerTarget;
  setTrackMarkerTarget(maxP);
  if (Math.abs(trackMarkerTarget - prevTarget) > 1e-3) changed = true;

  return changed;
}

function updateSound(dt) {
  if (!sound) return;

  const sp = inputController.getLastPointerRef();
  Utils.screenToWorldOut(mouseWorld, sp.x, sp.y, camera, viewport);

  const active = new Set();

  soundField.computeTargets({
    mouseWorld,
    stageById: pointById,
    onTarget: (id, v) => {
      active.add(id);
      sound.setTarget(id, v);
    },
  });

  for (const id of SOUND_IDS) {
    if (!active.has(id)) sound.setTarget(id, id === '0막' ? SOUND.BASE_MIN : 0);
  }
  sound.update(dt);
}

// 이벤트 바인딩
function bindEvents() {
  window.addEventListener('resize', resize);

  btnZoomIn?.addEventListener('click', (e) => {
    e.preventDefault();
    inputController.zoomBy(CAMERA.ZOOM_STEP, 'center');
  });

  btnZoomOut?.addEventListener('click', (e) => {
    e.preventDefault();
    inputController.zoomBy(1 / CAMERA.ZOOM_STEP, 'center');
  });

  btnSound?.addEventListener('click', onToggleSound);

  btnStory?.addEventListener('click', (e) => {
    e.preventDefault();
    toggleStory();
  });

  document.addEventListener('pointerdown', (e) => {
    if (!storyOpen) return;
    if (storyPanel?.contains(e.target)) return;
    if (btnStory.contains(e.target)) return;
    closeStory();
  });
}

// 렌더링
function clearCanvas() {
  // ctx.globalCompositeOperation = 'source-over';
  // ctx.globalAlpha = 1;
  // ctx.fillStyle = '#6dc396';
  // ctx.fillRect(0, 0, viewport.width, viewport.height);
  ctx.globalCompositeOperation = 'source-over';
  ctx.globalAlpha = 1;
  ctx.clearRect(0, 0, viewport.width, viewport.height);
}

function drawMainScene() {
  beginCamera(ctx, camera, viewport);
  scene.draw(ctx, sceneAlpha);
  endCamera(ctx);
}

function drawFrame() {
  clearCanvas();

  // background.draw(ctx, viewport);

  if (MODE === 'preload' || MODE === 'reveal') {
    preloader.draw(ctx, viewport, camera);
    return;
  }

  drawMainScene();
}

// 업데이트
function updatePreload(dt) {
  let changed = false;

  // Scale
  const prevS = camera.scale;
  camera.scale = Utils.lerpExp(camera.scale, camera.ts, camera.zoomSpeed, dt);
  if (Math.abs(camera.scale - prevS) > 1e-5) {
    clampCameraToWorld(camera, viewport, worldBounds, CAMERA.ALLOW_FACTOR);
    changed = true;
  }

  changed = preloader.update(dt) || changed;

  if (preloader.isDone()) {
    sceneAlpha = 0;
    setMode('main');
    return true;
  }
  return true;
}

function updateMain(dt) {
  let changed = false;

  // 카메라 이동
  const prevX = camera.x;
  const prevY = camera.y;

  camera.x = Utils.lerpExp(camera.x, camera.tx, camera.moveSpeed, dt);
  camera.y = Utils.lerpExp(camera.y, camera.ty, camera.moveSpeed, dt);

  if (Math.abs(camera.x - prevX) > 1e-3 || Math.abs(camera.y - prevY) > 1e-3) {
    clampCameraToWorld(camera, viewport, worldBounds, CAMERA.ALLOW_FACTOR);
    changed = true;
  }

  sceneAlpha = Utils.lerpExp(sceneAlpha, 1, SCENE_IN_SPEED, dt);

  if (sceneAlpha < 0.999) {
    changed = true;
  } else {
    if (sceneAlpha !== 1) changed = true;
    sceneAlpha = 1;
  }

  changed = scene.update(dt) || changed;

  if (!IS_MOBILE) {
    changed = computeSoundField() || changed;
    if (sound && soundEnabled) {
      updateSound(dt);
      changed = true;
    }
  }

  // 마커 움직임
  if (!IS_MOBILE) {
    const prevMarkerPos = trackMarkerPos;
    trackMarkerPos = Utils.lerpExp(trackMarkerPos, trackMarkerTarget, 6, dt);
    if (Math.abs(trackMarkerPos - prevMarkerPos) > 1e-4 && trackMarker) {
      trackMarker.style.left = `${trackMarkerPos * 100}%`;
      changed = true;
    }
  }

  return changed;
}

function update(dt) {
  if (MODE === 'preload' || MODE === 'reveal') {
    return updatePreload(dt);
  }
  return updateMain(dt);
}

// 룹
function tick(now) {
  const dt = Math.min(0.05, (now - lastTime) / 1000);
  lastTime = now;

  const changed = update(dt);

  if (changed || needsRender) {
    needsRender = false;
    drawFrame();
  }

  requestAnimationFrame(tick);
}

function init() {
  resize();
  fitWorldContain();

  inputController.attach();
  inputController.setEnabled(false);

  stageNav = mountStageNav({
    root: document.querySelector('.story .story-panel') || document,
    canSelect: (id) => pointById.has(id),
    onSelect: (stageId) => {
      cameraStageCtrl.moveToStage(stageId);
    },
  });
  const storyModalCtrl = mountStoryPapers({
    modal: document.querySelector('.story-modal'),
    stageTplMap: {
      '0막': 'tpl-stage-0',
      '0막-영상': 'tpl-stage-0-video',
      '1막': 'tpl-stage-1',
      '2-1막': 'tpl-stage-2',
      '2-2막': 'tpl-stage-2-2',
      '3막': 'tpl-stage-3',
      '4-1막': 'tpl-stage-4',
      '4-2막': 'tpl-stage-4-2',
      '5-1막': 'tpl-stage-5',
      '5-2막': 'tpl-stage-5-2',
    },
    stageOrder: ['0막', '1막', '2-1막', '3막', '4-1막', '5-1막'],
    onOpen: (stageId, paperId, paper) => {
      if (sound && soundEnabled) {
        wasSoundOn = true;
        sound.setMaster(0);
      } else {
        wasSoundOn = false;
      }
      if (storyOpen) closeStory();

      bookStackCtrl?.destroy?.();
      bookStackCtrl = null;

      stage52GalleryCtrl?.destroy?.();
      stage52GalleryCtrl = null;

      if (stageId === '0막') {
        stage0HoverCtrl = mountStage0Hover({
          root: paper,
          isMobile: IS_MOBILE,
        });
      }

      if (stageId === '2-2막') {
        stage22AudioCtrl = createStage22AudioController({
          paper,
          STAGE22_AUDIO,
          BOOK_AUDIO,
        });

        stage22AudioCtrl.onActiveChange(paper?.classList?.contains('active'));

        bookStackCtrl = mountBookStack({
          root: paper,
          booksData: BOOK_PAGES,
          onSelectBook: stage22AudioCtrl.onSelectBook,
          onCloseViewer: stage22AudioCtrl.onCloseViewer,
          enableHover: !IS_MOBILE,
        });
      }

      if (stageId === '5-2막') {
        stage52GalleryCtrl = mountStage52Gallery({
          root: paper,
        });
        stage52GalleryCtrl?.refreshAll?.();
      }
    },
    onClose: () => {
      stage22AudioCtrl?.destroy();
      stage22AudioCtrl = null;

      stage52GalleryCtrl?.destroy?.();
      stage52GalleryCtrl = null;

      stage0HoverCtrl?.destroy?.();
      stage0HoverCtrl = null;

      if (sound && wasSoundOn) {
        sound.setMaster(1);
      }

      bookStackCtrl?.destroy?.();
      bookStackCtrl = null;

      if (IS_MOBILE && lastTappedPointId) {
        pointById.get(lastTappedPointId)?.setActive?.(false);
        lastTappedPointId = null;
      }
    },

    onActiveChange: (stageId, paperId, paper, isActive) => {
      if (stageId === '2-2막') {
        stage22AudioCtrl?.onActiveChange(isActive);
      }
    },
  });

  inputController.setPointClickHandler({
    canPointClick: (point) => {
      if (IS_MOBILE) return true;
      return point.active || point.mix >= POINT.SWAP_ON;
    },
    onPointClick: (stageId, point) => {
      if (IS_MOBILE) {
        if (lastTappedPointId && lastTappedPointId !== stageId) {
          pointById.get(lastTappedPointId)?.setActive?.(false);
        }
        point?.setActive?.(true);
        lastTappedPointId = stageId;
      }
      if (storyOpen) closeStory();
      storyModalCtrl.openGroup(stageId);
    },
  });

  document.addEventListener(
    'click',
    async (e) => {
      const btn = e.target.closest(
        '.story-link button[data-stage], .btn-work[data-stage]',
      );

      if (!btn) return;

      e.preventDefault();
      e.stopPropagation();

      const stageId = btn.dataset.stage;
      if (!stageId) return;

      await storyModalCtrl.close?.();
      storyModalCtrl.openGroup(stageId);
    },
    true,
  );

  const artistAcc = mountArtistAccordion({ root: document });

  mediaCtrl = mountMediaControls({
    root: document,
    onAnyMediaPlay: () => {
      if (sound && soundEnabled) sound.setMaster(0);
    },
  });

  // background.start().finally(() => {
  //   preloader.start();
  //   invalidate();
  // });

  preloadImage(BACKGROUND_URL).finally(() => {
    preloader.start();
    invalidate();
  });

  if (!IS_MOBILE) initSound().catch(console.error);

  bindEvents();

  setMode('preload');

  requestAnimationFrame(tick);
}

init();
